import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  fetchSignInMethodsForEmail
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { isCancelledPopup } from '../utils/authErrors';

/**
 * Social sign-in for the admin dashboard.
 *
 * Deliberately thinner than the customer site's copy
 * (socialsnap.pics/src/services/auth.js): that one bootstraps a `users`
 * document on first sign-in because anyone may sign up there. The admin app
 * never provisions accounts — staff arrive through an invite — so this only
 * authenticates. AuthContext then gates on role and signs unauthorized users
 * back out.
 */

const PROVIDER_LABELS = {
  facebook: 'Facebook',
  google: 'Google',
  apple: 'Apple'
};

const SIGN_IN_METHOD_LABELS = {
  'facebook.com': 'Facebook',
  'google.com': 'Google',
  'apple.com': 'Apple',
  password: 'email and password'
};

const buildProvider = (providerId) => {
  switch (providerId) {
    case 'facebook': {
      const provider = new FacebookAuthProvider();
      provider.addScope('email');
      return provider;
    }
    case 'google': {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.setCustomParameters({ prompt: 'select_account' });
      return provider;
    }
    case 'apple': {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      return provider;
    }
    default:
      throw new Error(`Unsupported auth provider: ${providerId}`);
  }
};

// Preserve the originating Firebase code on the user-facing error so callers
// can tell an expected outcome (popup blocked, wrong provider) from a fault
// worth reporting to Sentry. `userFacing` tells authErrorMessage this message
// was written for a person and beats its per-code table — which matters for
// account-exists-with-different-credential, where we can name the provider the
// account actually uses and the table can't.
const authError = (message, code) =>
  Object.assign(new Error(message), { code, userFacing: true });

const buildAccountExistsMessage = async (error) => {
  const email = error.customData?.email;

  if (email) {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      if (methods.length > 0) {
        const labels = methods
          .map((method) => SIGN_IN_METHOD_LABELS[method] || method)
          .join(' or ');
        return `An account already exists with this email. Please sign in with ${labels}.`;
      }
    } catch {
      // Fall through to the generic message
    }
  }

  return 'An account already exists with this email using a different sign-in method.';
};

/**
 * Authenticate with a social provider ('facebook' | 'google' | 'apple') via popup.
 *
 * Returns { user, isNewUser } on success, or { user: null, cancelled: true } if
 * the user closed the popup. Throws an Error with a user-displayable .message
 * otherwise.
 *
 * `isNewUser` matters here: Firebase social sign-in has no sign-in-only mode —
 * an unrecognized identity always mints an Auth account. The dashboard has no
 * self-serve signup, so the caller is expected to reject and delete new users
 * rather than let this page become an account-creation surface.
 */
export const signInWithProvider = async (providerId) => {
  const label = PROVIDER_LABELS[providerId] || providerId;
  const provider = buildProvider(providerId);

  try {
    const result = await signInWithPopup(auth, provider);
    return {
      user: result.user,
      isNewUser: getAdditionalUserInfo(result)?.isNewUser === true,
    };
  } catch (error) {
    if (isCancelledPopup(error)) {
      return { user: null, cancelled: true };
    }

    console.error(`${label} sign-in error:`, error);

    if (error.code === 'auth/popup-blocked') {
      throw authError('Popup blocked. Please allow popups for this site.', error.code);
    }

    if (error.code === 'auth/account-exists-with-different-credential') {
      throw authError(await buildAccountExistsMessage(error), error.code);
    }

    throw authError(`Failed to sign in with ${label}. Please try again.`, error.code);
  }
};
